import * as React from 'react';
import { cn } from '../utils/cn';
import {
  Settings,
  Eye,
  EyeOff,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
  Trash2,
  Info,
  PlusCircle,
  Plus,
  Minus,
  Search,
  ArrowLeft,
} from 'lucide-react';

/**
 * Maps antd icon type names to lucide-react components.
 * Extend this map as you migrate more components.
 */
const iconMap: Record<string, React.ComponentType<any>> = {
  setting: Settings,
  settings: Settings,
  eye: Eye,
  'eye-invisible': EyeOff,
  up: ChevronUp,
  down: ChevronDown,
  left: ChevronLeft,
  right: ChevronRight,
  close: X,
  delete: Trash2,
  'info-circle': Info,
  'plus-circle': PlusCircle,
  plus: Plus,
  minus: Minus,
  search: Search,
  'arrow-left': ArrowLeft,
};

type IconSize = 'sm' | 'md' | 'lg' | 'xl';

const sizeMap: Record<IconSize, number> = {
  sm: 16,
  md: 20,
  lg: 24,
  xl: 32,
};

export interface IconProps {
  /** Antd compat: icon type name (e.g. "setting", "delete") */
  type?: string;
  /** Custom SVG component (antd compat: component={MySvg}) — accepts Lucide icons and plain SVG components */
  component?: React.ComponentType<any>;
  /** Antd compat: "filled" | "outlined" | "twoTone" — ignored, kept for compat */
  theme?: string;
  /** Size: named token ("sm"|"md"|"lg"|"xl") or number in px. Defaults to 1em for custom components, 20px for Lucide. */
  size?: IconSize | number;
  /** Additional class names */
  className?: string;
  /** Inline style */
  style?: React.CSSProperties;
  /** Click handler */
  onClick?: React.MouseEventHandler;
  /** Accessible label for interactive icons */
  'aria-label'?: string;
  /** Antd compat: two-tone color — ignored, kept for compat */
  twoToneColor?: string;
}

export function Icon({
  type,
  component: CustomComponent,
  theme: _theme,
  size,
  className,
  style,
  onClick,
  'aria-label': ariaLabel,
}: IconProps) {
  const resolvedSize = typeof size === 'string' ? sizeMap[size] : size;
  const isInteractive = !!onClick;
  const a11yProps = {
    role: isInteractive ? 'button' as const : undefined,
    'aria-label': ariaLabel,
    'aria-hidden': (!ariaLabel && !isInteractive) ? true as const : undefined,
    tabIndex: (isInteractive && !ariaLabel) ? undefined : undefined,
  };

  // Custom SVG component (e.g. <Icon component={BackIcon} />)
  if (CustomComponent) {
    return (
      <span
        className={cn('anticon text-icon', className)}
        style={style}
        onClick={onClick}
        {...a11yProps}
      >
        <CustomComponent
          width={resolvedSize || '1em'}
          height={resolvedSize || '1em'}
          style={{ display: 'inline-block', verticalAlign: '-0.125em' }}
        />
      </span>
    );
  }

  // Mapped lucide icon (e.g. <Icon type="delete" />)
  if (type) {
    const LucideIcon = iconMap[type];
    if (LucideIcon) {
      return (
        <span
          className={cn('anticon text-icon', className)}
          style={style}
          onClick={onClick}
          {...a11yProps}
        >
          <LucideIcon size={resolvedSize || 20} />
        </span>
      );
    }

    // Unmapped icon type — log in dev, render nothing
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[Icon] No mapping for antd icon type="${type}". Add it to icon.tsx iconMap.`);
    }
    return null;
  }

  return null;
}
